import axios from "axios";

import { getAppBaseURL } from "./networkUtil";

import {
  HANDLED_ERROR_CODES,
  HTTP_STATUS_CODE,
} from "../constants/apiResponseCode.constants";

import { storeGlobalApiErrorCode } from "../Store/TemplateStore/template.action";
import CAStore from "../Store/TemplateStore/template.store";

const AXIOS_TIMEOUT = { timeout: 15 * 1000 * 60 };

const SERVER_BASE_URL = process.env.REACT_APP_URL + "/advisor/api/v1/";

// TODO: need to verify why this is needed
const validateStatusFn = function (status) {
  return status >= 200 && status <= 422; // Treat status codes 200-299 as successful
};

const AxiosInstance = axios.create({
  baseURL: `${SERVER_BASE_URL}`,
  timeout: AXIOS_TIMEOUT,
  validateStatus: validateStatusFn,
});

const updateGlobalApiErrorCode = (statusCode) => {
  const currentCode = CAStore.getState()?.globalApiErrorCode;
  if (currentCode !== statusCode) {
    CAStore.dispatch(storeGlobalApiErrorCode(statusCode));
  }
};

const getStoreToken = () => {
  const stateToken = CAStore.getState()?.token;
  return typeof stateToken === "string"
    ? stateToken
    : stateToken?.token ?? null;
};

/**
 * Request interceptor — dynamic baseURL rewrite.
 *
 * Most requests use an app-scoped base URL derived from the current route.
 * Request callers can override this by passing `overrideAppScope`
 * with one of: "ca", "ra", or "base".
 * If `overrideAppScope` is not provided, scope is auto-detected from route.
 *
 * However, some API calls (e.g. getadvanceSettings / setadvanceSettings)
 * are app-agnostic (shared across base, ca & ra).
 * Those callers pass `overrideAppScope: "base"` to preserve
 * the plain /advisor/api/v1/ base.
 */
/**
 * Global auth gate:
 * If token is not available yet, protected API requests are cancelled.
 * This avoids early 401 calls during page refresh before token initialization.
 */
AxiosInstance.interceptors.request.use(
  (config) => {
    const { overrideAppScope } = config;
    config.baseURL = getAppBaseURL(overrideAppScope);

    const storeToken = getStoreToken();

    if (storeToken) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${storeToken}`,
      };

      return config;
    }

    const cancelError = new axios.Cancel(
      "Blocked API request before auth token initialization.",
    );

    cancelError.config = config;

    return Promise.reject(cancelError);
  },
  (error) => Promise.reject(error),
);

AxiosInstance.interceptors.response.use(
  (response) => {
    // validateStatusFn treats 200-422 as success, so capture 401 here
    // and let global routing render the unauthorized full-page experience.
    if (response.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
      updateGlobalApiErrorCode(HTTP_STATUS_CODE.UNAUTHORIZED);
    }

    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const status = error?.response?.status;

    if (HANDLED_ERROR_CODES.includes(status)) {
      updateGlobalApiErrorCode(status);
    }
    return Promise.reject(error);
  },
);

export const AxiosInstanceWithoutTimeout = axios.create({
  baseURL: `${getAppBaseURL()}`,
});

export default AxiosInstance;
